import { useState, useEffect } from 'react';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PageShell } from '../components/PageShell.tsx';
import { LoadingDots } from '../components/LoadingDots.tsx';
import { useTrainingPlan } from '../hooks/useTrainingPlan.ts';
import { useStore } from '../lib/store.ts';
import type { TrainingFormat, TrainingLevel, TrainingPlanResponse } from '../types/api.ts';

const GOAL_KEYS = [
  { value: 'endurance', key: 'training.goalEndurance' },
  { value: 'ftp_improvement', key: 'training.goalFTP' },
  { value: 'weight_loss', key: 'training.goalWeight' },
  { value: 'sprint_power', key: 'training.goalSprint' },
  { value: 'climbing', key: 'training.goalClimbing' },
  { value: 'general_fitness', key: 'training.goalFitness' },
] as const;

const LEVEL_KEYS: { value: TrainingLevel; key: string }[] = [
  { value: 'beginner', key: 'training.beginner' },
  { value: 'intermediate', key: 'training.intermediate' },
  { value: 'advanced', key: 'training.advanced' },
];

const DAY_KEYS = [
  'training.monday',
  'training.tuesday',
  'training.wednesday',
  'training.thursday',
  'training.friday',
  'training.saturday',
  'training.sunday',
] as const;

const DAYS_API = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const FORMAT_KEYS: { value: TrainingFormat; emoji: string; labelKey: string; descKey: string }[] = [
  { value: 'text', emoji: '\u{1F4DD}', labelKey: 'training.formatText', descKey: 'training.formatTextDesc' },
  { value: 'json', emoji: '\u{1F5C2}\uFE0F', labelKey: 'training.formatJSON', descKey: 'training.formatJSONDesc' },
  { value: 'pdf', emoji: '\u{1F4C4}', labelKey: 'training.formatPDF', descKey: 'training.formatPDFDesc' },
  { value: 'ical', emoji: '\u{1F4C5}', labelKey: 'training.formatCalendar', descKey: 'training.formatCalendarDesc' },
];

const LOADING_MSG_KEYS = [
  'training.loadingMsg1',
  'training.loadingMsg2',
  'training.loadingMsg3',
  'training.loadingMsg4',
] as const;

function getTomorrow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

const ZONE_COLORS: Record<string, string> = {
  'Zone 1': 'bg-blue-500/20 text-blue-400',
  'Zone 2': 'bg-green-500/20 text-green-400',
  'Tempo': 'bg-orange-500/20 text-orange-400',
  'Zone 3': 'bg-orange-500/20 text-orange-400',
  'Threshold': 'bg-red-500/20 text-orange-300',
  'Zone 4': 'bg-red-500/20 text-orange-300',
  'VO2max': 'bg-red-600/20 text-red-400',
  'Zone 5': 'bg-red-600/20 text-red-400',
  'Recovery': 'bg-blue-400/20 text-blue-300',
  'Rest': 'bg-gray-500/20 text-gray-400',
};

function zoneBadge(zone: string) {
  const color = ZONE_COLORS[zone] ?? 'bg-gray-500/20 text-gray-400';
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>{zone}</span>;
}

const pillBase = 'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors cursor-pointer';
const pillActive = 'bg-blue-500/20 text-blue-400 border-blue-500';
const pillInactive = 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border) hover:border-gray-400';

const inputClass = 'w-full bg-(--color-bg-tertiary) text-(--color-text-primary) rounded-lg px-4 py-3 text-sm border border-(--color-border) focus:outline-none focus:border-(--color-accent)';

export function Training() {
  const { t } = useTranslation();
  const storedFTP = useStore((s) => s.ftp);
  const profileFTP = useStore((s) => s.user?.profile?.ftp ?? null);
  const profileLevel = useStore((s) => s.user?.profile?.level ?? null);
  const profileGoals = useStore((s) => s.user?.profile?.goals ?? null);

  // Local overrides — null means "use profile value"
  const [ftpOverride, setFtpOverride] = useState<string | null>(null);
  const [levelOverride, setLevelOverride] = useState<TrainingLevel | null>(null);
  const [goalsOverride, setGoalsOverride] = useState<string[] | null>(null);

  // Derived values: local override wins, then profile, then defaults
  const ftp = ftpOverride ?? (storedFTP ?? profileFTP)?.toString() ?? '';
  const level = levelOverride ?? (profileLevel as TrainingLevel) ?? 'intermediate';
  const goals = goalsOverride ?? profileGoals ?? [];

  const setFtp = (v: string) => setFtpOverride(v);
  const setLevel = (v: TrainingLevel) => setLevelOverride(v);
  const setGoals = (v: string[]) => setGoalsOverride(v);
  const [hours, setHours] = useState('8');
  const [weeks, setWeeks] = useState('4');
  const [availableDays, setAvailableDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [format, setFormat] = useState<TrainingFormat>('text');
  const [startDate, setStartDate] = useState(getTomorrow());
  const [loadingMsg, setLoadingMsg] = useState(0);

  const mutation = useTrainingPlan();

  useEffect(() => {
    if (!mutation.isPending) return;
    const reset = setTimeout(() => setLoadingMsg(0), 0);
    const interval = setInterval(() => {
      setLoadingMsg((i) => (i + 1) % LOADING_MSG_KEYS.length);
    }, 4000);
    return () => { clearTimeout(reset); clearInterval(interval); };
  }, [mutation.isPending]);

  const toggleGoal = (goal: string) => {
    const next = goals.includes(goal) ? goals.filter((g) => g !== goal) : [...goals, goal];
    setGoals(next);
  };

  const toggleDay = (idx: number) =>
    setAvailableDays((prev) => (prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx]));

  const allDaysSelected = availableDays.length === DAYS_API.length;

  const isDisabled =
    mutation.isPending || goals.length === 0 || (format === 'ical' && !startDate);

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!ftp || isDisabled) return;
    mutation.mutate({
      ftp: Number.parseInt(ftp, 10),
      goals,
      hours_per_week: Number.parseInt(hours, 10),
      weeks: Number.parseInt(weeks, 10),
      format,
      level,
      available_days: availableDays.map((i) => DAYS_API[i]),
      ...(format === 'ical' ? { start_date: startDate } : {}),
    });
  };

  const isFileFormat = format === 'pdf' || format === 'ical';

  return (
    <PageShell title={t('training.title')} subtitle={t('training.subtitle')}>
      <form onSubmit={handleSubmit} className="space-y-6 mb-8">
        {/* Section 1: Basic Info */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-(--color-text-secondary) mb-1.5">{t('training.ftp')}</label>
            <input
              type="number"
              value={ftp}
              onChange={(e) => setFtp(e.target.value)}
              placeholder={t('training.ftpPlaceholder')}
              min={50}
              max={500}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-(--color-text-secondary) mb-2">{t('training.level')}</label>
            <div className="flex flex-wrap gap-2">
              {LEVEL_KEYS.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  onClick={() => setLevel(l.value)}
                  className={`${pillBase} ${level === l.value ? pillActive : pillInactive}`}
                >
                  {t(l.key)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-(--color-text-secondary) mb-1.5">{t('training.hoursPerWeek')}</label>
              <input
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                min={1}
                max={40}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-(--color-text-secondary) mb-1.5">{t('training.durationWeeks')}</label>
              <input
                type="number"
                value={weeks}
                onChange={(e) => setWeeks(e.target.value)}
                min={1}
                max={52}
                required
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Section 2: Goals */}
        <div>
          <label className="block text-sm font-medium text-(--color-text-secondary) mb-2">{t('training.goals')}</label>
          <div className="flex flex-wrap gap-2">
            {GOAL_KEYS.map((g) => (
              <button
                key={g.value}
                type="button"
                onClick={() => toggleGoal(g.value)}
                className={`${pillBase} ${goals.includes(g.value) ? pillActive : pillInactive}`}
              >
                {t(g.key)}
              </button>
            ))}
          </div>
        </div>

        {/* Section 3: Available Days */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-(--color-text-secondary)">{t('training.availableDays')}</label>
            <button
              type="button"
              onClick={() => setAvailableDays(allDaysSelected ? [] : [0, 1, 2, 3, 4, 5, 6])}
              className="text-xs text-(--color-accent) hover:text-(--color-accent-dim) transition-colors"
            >
              {allDaysSelected ? t('training.clearAll') : t('training.selectAll')}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {DAY_KEYS.map((key, idx) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleDay(idx)}
                className={`${pillBase} ${availableDays.includes(idx) ? pillActive : pillInactive}`}
              >
                {t(key)}
              </button>
            ))}
          </div>
        </div>

        {/* Section 4: Output Format */}
        <div>
          <label className="block text-sm font-medium text-(--color-text-secondary) mb-2">{t('training.outputFormat')}</label>
          <div className="grid grid-cols-2 gap-3">
            {FORMAT_KEYS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFormat(f.value)}
                className={`flex flex-col items-center gap-1 p-4 rounded-xl border text-center transition-colors ${
                  format === f.value
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-(--color-border) bg-(--color-bg-tertiary) hover:border-gray-400'
                }`}
              >
                <span className="text-2xl">{f.emoji}</span>
                <span className="text-sm font-medium text-(--color-text-primary)">{t(f.labelKey)}</span>
                <span className="text-xs text-(--color-text-secondary)">{t(f.descKey)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Section 5: Start Date (ical only) */}
        {format === 'ical' && (
          <div>
            <label className="block text-sm font-medium text-(--color-text-secondary) mb-1.5">{t('training.startDate')}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputClass}
            />
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isDisabled}
          className="w-full bg-green-400 text-gray-900 py-3.5 rounded-xl font-bold text-base shadow-lg shadow-(--color-accent)/30 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none transition-all"
        >
          {t('training.generate')}
        </button>
      </form>

      {/* Loading */}
      {mutation.isPending && (
        <div className="text-center py-8">
          <LoadingDots text={t(LOADING_MSG_KEYS[loadingMsg])} />
        </div>
      )}

      {/* Output */}
      {mutation.data && <PlanOutput data={mutation.data} format={format} isFile={isFileFormat} />}
    </PageShell>
  );
}

function PlanOutput({ data, format, isFile }: Readonly<{ data: TrainingPlanResponse; format: TrainingFormat; isFile: boolean }>) {
  const { t } = useTranslation();

  if (isFile) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 flex items-center gap-3">
        <span className="text-green-400 text-2xl shrink-0">{'\u2705'}</span>
        <p className="text-sm text-green-300 font-medium">{t('training.fileDownloaded')}</p>
      </div>
    );
  }

  if (format === 'text' && data.plan) {
    return (
      <div className="bg-(--color-bg-secondary) border border-(--color-border) rounded-xl p-6">
        <h3 className="text-lg font-bold text-(--color-accent) mb-4">{t('training.yourPlan')}</h3>
        <pre className="text-sm text-(--color-text-primary) whitespace-pre-wrap font-mono leading-relaxed">
          {data.plan}
        </pre>
      </div>
    );
  }

  if (format === 'json' && data.weeks) {
    return (
      <div className="space-y-6">
        {data.weeks.map((week) => (
          <div key={week.week_number} className="bg-(--color-bg-secondary) border border-(--color-border) rounded-xl overflow-hidden">
            <div className="bg-blue-500/15 px-5 py-3">
              <h3 className="text-sm font-bold text-blue-400">{t('training.weekLabel')} {week.week_number}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-(--color-border) text-(--color-text-secondary)">
                    <th className="text-left px-4 py-2 font-medium">{t('training.colDay')}</th>
                    <th className="text-left px-4 py-2 font-medium">{t('training.colWorkout')}</th>
                    <th className="text-left px-4 py-2 font-medium">{t('training.colDuration')}</th>
                    <th className="text-left px-4 py-2 font-medium">{t('training.colPower')}</th>
                    <th className="text-left px-4 py-2 font-medium">{t('training.colZone')}</th>
                  </tr>
                </thead>
                <tbody>
                  {week.workouts.map((w) => (
                    <tr key={`${week.week_number}-${w.day}`} className="border-b border-(--color-border)/50 last:border-0">
                      <td className="px-4 py-2.5 text-(--color-text-primary) font-medium">{w.day}</td>
                      <td className="px-4 py-2.5 text-(--color-text-primary)">{w.title}</td>
                      <td className="px-4 py-2.5 text-(--color-text-secondary)">{w.duration} {t('training.min')}</td>
                      <td className="px-4 py-2.5 text-(--color-text-secondary)">{w.power}</td>
                      <td className="px-4 py-2.5">{zoneBadge(w.zone)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
}
